import type { ISession } from '@newo/api/gateway';
import type { DataTableColumn } from 'mantine-datatable';
import { Box, Text, Tooltip } from '@mantine/core';
import { CircleQuestionMark } from 'lucide-react';
import { getConversationSuccessScore } from '@/pages/Sessions/Sessions.qualityMetrics.ts';
import { cn } from '@/utils';

const CSS_COLUMN_ACCESSOR = 'css';
const CSS_COLUMN_TOOLTIP_LABEL = 'Conversation Success Score';

const CSS_SCORE_UNAVAILABLE_LABEL = 'N/A';
const CSS_COLUMN_WIDTH = 100;
const CSS_WARNING_SCORE_THRESHOLD = 60;
const CSS_DANGER_SCORE_THRESHOLD = 40;
const CSS_TOOLTIP_ICON_SIZE = 14;

type CssColumn = DataTableColumn<ISession> & {
    accessor: typeof CSS_COLUMN_ACCESSOR;
};

export const getCssColumn = (): CssColumn => ({
    title: (
        <Box className="inline-flex items-center gap-1" component="span">
            <Text inherit component="span">
                CSS
            </Text>
            <Tooltip withArrow label={CSS_COLUMN_TOOLTIP_LABEL} position="top">
                <Box
                    aria-label={CSS_COLUMN_TOOLTIP_LABEL}
                    className="inline-flex items-center text-gray-6"
                    component="span"
                    data-testid="sessions-css-column-tooltip-trigger"
                >
                    <CircleQuestionMark
                        aria-hidden
                        size={CSS_TOOLTIP_ICON_SIZE}
                    />
                </Box>
            </Tooltip>
        </Box>
    ),
    accessor: CSS_COLUMN_ACCESSOR,
    draggable: true,
    resizable: true,
    hidden: false,
    width: CSS_COLUMN_WIDTH,
    render: (session) => {
        const cssScore = getConversationSuccessScore(
            session.arguments.quality_metrics
        );

        if (cssScore === undefined) {
            return (
                <Text
                    inherit
                    className="text-gray-6"
                    component="span"
                    data-testid={`session-css-score-${session.id}`}
                >
                    {CSS_SCORE_UNAVAILABLE_LABEL}
                </Text>
            );
        }

        return (
            <Text
                inherit
                className={cn({
                    'text-green-6': cssScore >= CSS_WARNING_SCORE_THRESHOLD,
                    'text-orange-4':
                        cssScore < CSS_WARNING_SCORE_THRESHOLD &&
                        cssScore >= CSS_DANGER_SCORE_THRESHOLD,
                    'text-red-6': cssScore < CSS_DANGER_SCORE_THRESHOLD
                })}
                component="span"
                data-testid={`session-css-score-${session.id}`}
            >
                {cssScore} %
            </Text>
        );
    }
});
